import useSWR from "swr"
import { httpClient as axiosInstance, apiUrls, fetcher } from "@/src/api"
import { swrOptions } from "./leads"
import { useMemo } from "react"
import { FormSchemaAddUser, FormSchemaRolePermission } from "@/src/schemas/formSchemas"

export type UsersModel = {
    id: string
    name: string
    website: string
    address: string
    industry: string
    apnaOrganisationId: string
    phoneNumber: string
    createdBy: string
    createdDate: Date
    lastModifiedDate: Date
    updatedBy: string
    status: string
    email: string
}

type UsersInfo = {
    data: { users: [UsersModel] }
}

export function useGetUsers(companyId: string, page: number, size: number) {
    const url = `${apiUrls.USERS_LIST}?companyId=${companyId}&page=${page}&size=${size}`
    const { data, isLoading, error, isValidating } = useSWR<UsersInfo>(url, fetcher, swrOptions)

    const memoizedValue = useMemo(() => {
        return {
            users: data?.data,
            usersLoading: isLoading,
            usersError: error,
            usersValidating: isValidating,
        }
    }, [data, error, isLoading, isValidating])

    return memoizedValue
}

export const getUsers = async (companyId: string, page: number, size: number, query: string = "") => {
    const resp = await axiosInstance.get(
        `${apiUrls.USERS_LIST}?companyId=${companyId}&page=${page}&size=${size}${query ? `&search=${query}` : ""}`
    )
    return {
        users: resp?.data?.data,
        usersStatus: resp?.data?.status,
    }
}

export const addUser = (payload: FormSchemaAddUser) => {
    return axiosInstance.post(apiUrls.USERS_ADD(), payload)
}

export const editUser = (payload: FormSchemaAddUser, userId: string) => {
    return axiosInstance.patch(apiUrls.USERS_EDIT(userId), payload)
}

export const updateUser = (payload: FormSchemaAddUser, userId: string) => {
    return axiosInstance.patch(apiUrls.USERS_UPDATE(userId), payload)
}

export const updateRole = (payload: FormSchemaRolePermission) => {
    return axiosInstance.patch(apiUrls.USERS_ROLE_ASSIGN, payload)
}

export const getAllRoles = () => axiosInstance.get(apiUrls.USERS_ALL_ROLES)

export const updateTableRole = (role: string, permissionId: string) => {
    return axiosInstance.put(`${apiUrls.USERS_ROLE_ASSIGN}?role=${role}&permissionId=${permissionId}`)
}

export const deletePermission = (role: string, permissionId: string) => {
    return axiosInstance.delete(`${apiUrls.USERS_DELETE_PERMISSION}?role=${role}&permissionId=${permissionId}`)
}

export const getManagerList = async (role: string) => {
    const resp = await axiosInstance.get(`${apiUrls.USERS_MANAGER_LIST()}${role ? `?role=${role}` : ""}`)
    return {
        managerList: resp?.data?.data,
        managerListStatus: resp?.data?.status,
    }
}

export const fetchUserPermission = async () => {
    const resp = await axiosInstance.get(apiUrls.USERS_ROLE_PERMISSION)
    return {
        rolePermission: resp?.data?.data,
        rolePermissionStatus: resp?.data?.status,
    }
}
